import React, { useEffect, useState } from "react";
import { getDoctorByIdAPI } from "../../Api/MarketingApi";
import { getLocationAPI } from "../../Api/LookUp";
import Doctor from "./../../../images/doctorAvtar.png"
interface OriginalProfileProps {
  doctorId: number;
}

interface AreaOfInterest {
  role: string;
  description: string;
}

interface PreviousExperience {
  role: string;
  description: string;
}

interface DoctorDetails {
  empCode:string;
  createdBy:string;
  isSuccess: boolean;
  doctorId: number;
  locationId: number;
  locationName: string;
  languages: string;
  namePrefixCode: string;
  name: string;
  qualification: string;
  designation: string | null;
  areaOfInterest: string;
  awardsAchievements: string | null;
  researchPublications: string | null;
  previousExperiance: string;
  gender: string;
  specialization: string;
  doctorImage: string;
  profileBrief: string;
  phoneNo: string;
  emailId: string;
  experienceYears: number;
  doctorFees: number;
  status: string;
  registrationNo: string;
  feedback: string | null;
  secondaryLocations?: string | null;
  resumePath:string|null;
  departmentName:string;
}

interface Location {
  LocationId: number;
  LocationName: string;
  HospitalName?: string;
}

const OriginalProfile: React.FC<OriginalProfileProps> = ({ doctorId }) => {
  const userId = sessionStorage.getItem("userId");
  const [doctorData, setDoctorData] = useState<DoctorDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [secondaryLocationNames, setSecondaryLocationNames] = useState<{[key: number]: string}>({});
  const [primaryLocationName, setPrimaryLocationName] = useState<string>("");

  useEffect(() => {
    if (!userId) {
      return;
    }

    const fetchDoctorDetails = async () => {
      try {
        setIsLoading(true);
        setError("");
        const response = await getDoctorByIdAPI(doctorId, userId);
        setDoctorData(response.data.originalData);
        
        // Fetch location names
        const primaryLocId = response.data.originalData.locationId;
        const secondaryLocationIds = parseSecondaryLocations(response.data.originalData.secondaryLocations);
        
        // Fetch all location names (primary + secondary)
        fetchLocationNames(primaryLocId, secondaryLocationIds);
      } catch (error: any) {
        console.error("Error fetching doctor details:", error);
        setError(
          error?.response?.data?.message || "Failed to load doctor details"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchDoctorDetails();
  }, [doctorId, userId]);

  // Function to fetch both primary and secondary location names
  const fetchLocationNames = async (primaryLocId: number, secondaryLocationIds: number[]) => {
    try {
      const locationMap: {[key: number]: string} = {};
      
      // Fetch all locations
      const response = await getLocationAPI();
      
      if (response.data && Array.isArray(response.data)) {
        // Create a map of location ID to location name
        response.data.forEach((location: Location) => {
          // Store primary location name
          if (location.LocationId === primaryLocId) {
            setPrimaryLocationName(location.LocationName);
          }
          
          // Store secondary location names
          if (secondaryLocationIds.includes(location.LocationId)) {
            locationMap[location.LocationId] = location.LocationName;
          }
        });
        
        setSecondaryLocationNames(locationMap);
      }
    } catch (error) {
      console.error("Error fetching location names:", error);
    }
  };

  // Helper function to safely parse JSON strings
  const safeJSONParse = <T,>(jsonString: string | null, defaultValue: T): T => {
    if (!jsonString) return defaultValue;
    try {
      return JSON.parse(jsonString) as T;
    } catch (e) {
      console.error("JSON parse error:", e);
      return defaultValue;
    }
  };

  // Parse secondaryLocations from JSON string like "[{\"LocationId\":1},{\"LocationId\":2}]"
  const parseSecondaryLocations = (jsonString: string | null | undefined): number[] => {
    if (!jsonString) return [];
    try {
      const parsed = JSON.parse(jsonString);
      if (Array.isArray(parsed)) {
        return parsed.map((item: any) => item.LocationId || item.locationId).filter(Boolean);
      }
      return [];
    } catch (e) {
      console.error("Secondary locations parse error:", e);
      return [];
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 sticky top-6 overflow-hidden">
        <div className="bg-gradient-to-r from-[#009A85] to-[#00C9A7] px-6 py-4 border-b-4 border-[#008A75]">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Original Profile
          </h2>
        </div>
        <div className="p-6 flex items-center justify-center h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009A85] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading doctor details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !doctorData) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 sticky top-6 overflow-hidden">
        <div className="bg-gradient-to-r from-[#009A85] to-[#00C9A7] px-6 py-4 border-b-4 border-[#008A75]">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Original Profile
          </h2>
        </div>
        <div className="p-6 flex items-center justify-center h-[400px]">
          <div className="text-center">
            <svg
              className="w-16 h-16 text-red-500 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-red-600 font-semibold mb-2">
              {error || "No data available"}
            </p>
            <p className="text-gray-500 text-sm">Please try again later</p>
          </div>
        </div>
      </div>
    );
  }

  const areaOfInterest = safeJSONParse<AreaOfInterest[]>(
    doctorData.areaOfInterest,
    []
  );

  const previousExperience = safeJSONParse<PreviousExperience[]>(
    doctorData.previousExperiance,
    []
  );

  const awards = safeJSONParse<string[]>(doctorData.awardsAchievements, []);

  const publications = safeJSONParse<string[]>(
    doctorData.researchPublications,
    []
  );

  const secondaryLocationIds = parseSecondaryLocations(doctorData.secondaryLocations);

  const languages = doctorData.languages?.trim()
    ? doctorData.languages.split(",").map((l) => l.trim())
    : [];

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg border border-gray-200 sticky top-6 overflow-hidden">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-[#009A85] to-[#00C9A7] px-6 py-2 border-b-4 border-[#008A75]">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Original Profile
        </h2>
      </div>

      <div className="space-y-5 p-6 h-[calc(100vh-240px)] overflow-y-auto custom-scrollbar">
        {doctorData.feedback && (
          <div className="bg-gradient-to-r from-red-50 to-red-50 rounded-lg p-4 border border-red-200 shadow-sm">
            <p className="text-xs font-semibold text-red-700 uppercase mb-1 ">
              <span></span> Send Back Reason
            </p>
            <p className="text-xs text-gray-700 leading-relaxed">
              {doctorData.feedback}
            </p>
          </div>
        )}

        <div className="flex justify-between">
        {doctorData.doctorImage && (
          <div className="flex justify-center mb-4 w-35 h-40">
            <div className="relative">
              <div className="w-35 h-40 rounded-md overflow-hidden border-4 border-[#009A85] shadow-lg">
                <img
                  src= {doctorData.doctorImage
                                        ? `${process.env.REACT_APP_BASE_URL}/${doctorData.doctorImage}`
                                        : Doctor}

                 
                  alt={doctorData.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://via.placeholder.com/150?text=Doctor";
                  }}
                />
              </div>
              <div className="absolute bottom-0 right-0 bg-green-500 w-8 h-8 rounded-full border-4 border-white flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>
        )}  
{doctorData?.resumePath?.trim() && (
  <a
    href={doctorData.resumePath}
    target="_blank"
    rel="noopener noreferrer"
    download={`${doctorData.name}.pdf`}
    className="px-4 py-1 h-fit bg-blue-600 text-white rounded-md hover:bg-blue-700 transition inline-block"
  >
    Download CV
  </a>
)}
          </div>

        <div className="grid grid-cols-2 gap-3">
          <InfoCard
            icon=""
            label="Name"
            value={`${
              doctorData.namePrefixCode
                ? `${doctorData.namePrefixCode}\u00A0`
                : ""
            }${doctorData.name || "-"}`}
            bgColor="bg-blue-50"
            borderColor="border-blue-200"
          />
          <InfoCard
            icon=""
            label="Department Name"
            value={`${doctorData.departmentName||"-"}`}
            bgColor="bg-green-50"
            borderColor="border-green-200"
          />
          <InfoCard
            icon=""
            label="Specialization"
            value={doctorData.specialization || "-"}
            bgColor="bg-blue-50"
            borderColor="border-blue-200"
          />
           
          <InfoCard
            icon=""
            label="Designation"
            value={doctorData.designation || "-"}
            bgColor="bg-purple-50"
            borderColor="border-purple-200"
          />
          <InfoCard
            icon=""
            label="Experience"
            value={`${doctorData.experienceYears} Years`}
            bgColor="bg-green-50"
            borderColor="border-green-200"
          />
          <InfoCard
            icon=""
            label="Fee"
            value={`₹${doctorData.doctorFees || 0}`}
            bgColor="bg-orange-50"
            borderColor="border-orange-200"
          />
          <InfoCard
            icon=""
            label="Email"
            value={doctorData.emailId || "-"}
            bgColor="bg-cyan-50"
            borderColor="border-cyan-200"
          />
          <InfoCard
            icon="📱"
            label="Phone"
            value={doctorData.phoneNo || "-"}
            bgColor="bg-pink-50"
            borderColor="border-pink-200"
          />
          <InfoCard
            icon="🆔"
            label="Registration No"
            value={doctorData.registrationNo || "-"}
            bgColor="bg-pink-50"
            borderColor="border-pink-200"
          />
           <InfoCard
            icon=""
            label="Emp Code"
            value={doctorData.empCode || "-"}
            bgColor="bg-blue-50"
            borderColor="border-blue-200"
          />
          <InfoCard
            icon=""
            label="Created By"
            value={doctorData.createdBy || "-"}
            bgColor="bg-blue-50"
            borderColor="border-blue-200"
          />
        </div>

        {/* Primary Location Display */}
        <div className="bg-emerald-50 rounded-lg p-4 border-l-4 border-emerald-500 shadow-sm">
          <p className="text-xs font-semibold text-emerald-900 uppercase mb-2 flex items-center gap-2">
            <span>📍</span> Primary Location
          </p>
          <div className="flex items-center gap-2">
            <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-medium">
              {primaryLocationName || doctorData.locationName || `Location ID: ${doctorData.locationId}`}
            </span>
          </div>
        </div>

        {/* Secondary Locations Display with Location Names */}
        {secondaryLocationIds.length > 0 && (
          <div className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-500 shadow-sm">
            <p className="text-xs font-semibold text-purple-900 uppercase mb-2 flex items-center gap-2">
              <span>📍</span> Secondary Locations
            </p>
            <div className="flex flex-wrap gap-2">
              {secondaryLocationIds.map((locId, idx) => (
                <span
                  key={idx}
                  className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-medium"
                >
                  {secondaryLocationNames[locId] || `Location ID: ${locId}`}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Languages */}
        <div className="bg-indigo-50 rounded-lg p-4 border-l-4 border-indigo-500 shadow-sm">
          <p className="text-xs font-semibold text-indigo-900 uppercase mb-2 flex items-center gap-2">
            <span></span> Languages
          </p>
          <div className="flex flex-wrap gap-2">
            {languages.length > 0 ? (
              languages.map((lang, idx) => (
                <span
                  key={idx}
                  className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-medium"
                >
                  {lang}
                </span>
              ))
            ) : (
              <span className="text-xs text-gray-500">
                No languages specified
              </span>
            )}
          </div>
        </div>

        {/* Qualification */}
        {doctorData.qualification && (
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-4 border border-teal-200 shadow-sm">
            <p className="text-xs font-semibold text-teal-900 uppercase mb-2 flex items-center gap-2">
              <span></span> Qualification
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              {doctorData.qualification
                ?.split(";")
                .map((item: any) => item.trim())
                .filter(Boolean)
                .map((item: any, index: any) => (
                  <div key={index}>{item}</div>
                )) || (
                <span className="text-sm">No qualifications listed</span>
              )}
            </p>
          </div>
        )}

        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg p-4 border border-teal-200 shadow-sm">
          <p className="text-xs font-semibold text-teal-900 uppercase mb-2 flex items-center gap-2">
            <span></span> Profile Brief
          </p>
          <p className="text-sm text-gray-700 leading-relaxed">
            {doctorData?.profileBrief ? (
              doctorData.profileBrief
                .split(";")
                .map((q: any) => q.trim())
                .filter(Boolean)
                .map((q: any, index: any) => <div key={index}>{q}</div>)
            ) : (
              "-"
            )}
          </p>
        </div>

        {/* Area of Interest */}
        {areaOfInterest.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-2 px-1">
              <span className="text-lg"></span> Area of Interest
            </p>
            <div className="space-y-3">
              {areaOfInterest.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-lg p-4 border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-shadow"
                >
                  <p className="font-semibold text-sm text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    {item.role || "-"}
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed pl-4">
                    {item.description || "-"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Previous Experience */}
        {previousExperience.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-2 px-1">
              <span className="text-lg"></span> Previous Experience
            </p>
            <div className="space-y-3">
              {previousExperience.map((exp, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-lg p-4 border-l-4 border-green-500 shadow-sm hover:shadow-md transition-shadow"
                >
                  <p className="font-semibold text-sm text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    {exp.role || "-"}
                  </p>
                  <p className="text-sm text-gray-600 leading-relaxed pl-4">
                    {exp.description || "-"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {awards.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-2 px-1">
              <span className="text-lg"></span> Awards & Achievements
            </p>
            <div className="flex flex-wrap gap-2">
              {awards.map((award, idx) => (
                <div
                  key={idx}
                  className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-300 text-yellow-800 px-4 py-2 rounded-lg text-xs font-medium shadow-sm hover:shadow-md transition-shadow"
                >
                  <span className="mr-1">⭐</span> {award}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Publications */}
        {publications.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase mb-3 flex items-center gap-2 px-1">
              <span className="text-lg"></span> Research & Publications
            </p>
            <div className="space-y-2">
              {publications.map((pub, idx) => (
                <div
                  key={idx}
                  className="bg-gray-50 p-2 rounded-lg border border-gray-200 text-xs text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <span className="font-semibold text-gray-900">
                    {idx + 1}.
                  </span>{" "}
                  {pub}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Reusable Info Card Component
const InfoCard = ({
  icon,
  label,
  value,
  bgColor,
  borderColor,
}: {
  icon: string;
  label: string;
  value: string;
  bgColor: string;
  borderColor: string;
}) => (
  <div className={`${bgColor} rounded-lg p-3 border ${borderColor} shadow-sm`}>
    <p className="text-[10px] font-semibold text-gray-600 uppercase mb-1 flex items-center gap-1">
      {label}
    </p>
    <p
      className="text-sm font-semibold text-gray-900 truncate"
      title={value}
    >
      {value}
    </p>
  </div>
);

export default OriginalProfile;
